import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Search, CheckCircle2, Shield, FileSearch, Clock } from 'lucide-react';
import ChatPopup from '../components/ChatPopup';
import { trademarkSearchProcessSteps } from '../mockData';

const TrademarkSearch = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const benefits = [
    'Avoid costly rejections',
    'Identify potential conflicts',
    'Save time and money',
    'Professional analysis',
    'Comprehensive database search',
    'Peace of mind'
  ];

  const searchTypes = [
    { icon: FileSearch, title: 'Basic Search', desc: 'Quick USPTO database check for direct conflicts' },
    { icon: Search, title: 'Comprehensive Search', desc: 'Detailed analysis including common law and state marks' },
    { icon: Shield, title: 'International Search', desc: 'Global trademark databases and international registries' }
  ];

  const faqs = [
    {
      question: 'Why do I need a trademark search?',
      answer: 'A trademark search helps identify existing marks that might conflict with yours, reducing the risk of rejection, legal disputes, and the need to rebrand later.'
    },
    {
      question: 'How long does a search take?',
      answer: 'Basic searches can be completed in 1-2 business days. Comprehensive searches typically take 3-5 business days.'
    },
    {
      question: 'What does the search include?',
      answer: 'Our comprehensive search includes USPTO registered marks, pending applications, state registrations, and common law uses.'
    },
    {
      question: 'Is a trademark search required?',
      answer: 'While not legally required, a search is highly recommended to avoid costly conflicts and increase your chances of successful registration.'
    }
  ];

  return (
    <div className="min-h-screen">
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto">
                <Search className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Trademark Search Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Before filing your trademark application, ensure your mark is available with a thorough professional search.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => setIsChatOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                Get Started
              </Button>
              <Button size="lg" onClick={() => setIsChatOpen(true)} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What is a Trademark Search?</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base">
                <p>
                  A trademark search is a comprehensive investigation of existing trademarks to determine if your proposed mark is available for use and registration. This crucial step helps you avoid potential conflicts with existing marks.
                </p>
                <p>
                  Our professional trademark search goes beyond simple database queries. We analyze similar marks, phonetic equivalents, and common law uses that might not appear in official registries but could still pose legal challenges.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1639675973843-027a10e2f5a9" 
                alt="Professional workspace for trademark search and analysis"
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Search Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Search Options</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the search level that meets your needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {searchTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-orange-600 transition-colors">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{type.title}</h3>
                    <p className="text-gray-600">{type.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Search First?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Search className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Search Your Trademark?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Start with a professional search to protect your brand investment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setIsChatOpen(true)} className="bg-white text-orange-600 hover:bg-gray-100">
              Start Search
            </Button>
            <Button size="lg" onClick={() => setIsChatOpen(true)} variant="outline" className="border-white text-white hover:bg-white/10">
              Talk to an Expert
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrademarkSearch;
